import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { Dropdown } from '@components/index';
import Label from '@components/label/Label';
import { bloodGroupOptions } from '@features/member-management/constants';
import SelectDateInput from '@features/member-management/components/SelectDateInput';
import {
  CustomField,
  UserProfileCustomFieldType
} from '@features/member-management/types';
import { AddMemberFormDataType, ValidationError } from '@types';
import { isInvalidEmail } from '@utils/validations';

import Input from '../input/Input';

interface MemberPersonalInfoProps {
  formData: AddMemberFormDataType;
  handleFormDataChange: (
    field: keyof AddMemberFormDataType,
    value: string
  ) => void;
  setError: (error: ValidationError) => void;
  error: ValidationError;
  containerRef: React.RefObject<HTMLElement>;
  onFocused: () => void;
}

const MemberPersonalInfo = ({
  formData,
  handleFormDataChange,
  setError,
  error,
  containerRef,
  onFocused
}: MemberPersonalInfoProps) => {
  const { orgConfig } = useSelector((state: any) => state.user);
  const [isLastMonthForDob, setIsLastMonthForDob] = useState(false);

  const { email, dateOfBirth, bloodGroup } = formData;

  const shouldShowBloodGroup = !!orgConfig.member_custom_fields?.find(
    ({ key }: CustomField) => key === UserProfileCustomFieldType.BLOOD_GROUP
  );

  const onMemberEmailChange = (value: string) => {
    setError({ ...error, email: '' });
    handleFormDataChange('email', value);
  };

  const onEmailBlur = (value: string) => {
    if (value && isInvalidEmail(value)) {
      setError({ ...error, email: 'Invalid email' });
    }
  };

  const onDOBChange = (dob?: Date) => {
    handleFormDataChange('dateOfBirth', dob?.toLocaleDateString('en-CA') ?? '');
  };

  const onClearDOB = () => onDOBChange();

  const onBloodGroupChange = (value: string) => {
    handleFormDataChange('bloodGroup', value);
  };

  const getIsLastMonthForDob = (currentDate: Date) => {
    const today = new Date();
    const isLastMonth =
      currentDate.getMonth() === today.getMonth() &&
      currentDate.getFullYear() === today.getFullYear();
    setIsLastMonthForDob(isLastMonth);
  };

  useEffect(() => {
    getIsLastMonthForDob(dateOfBirth ? new Date(dateOfBirth) : new Date());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      role="presentation"
      ref={containerRef as React.RefObject<HTMLDivElement>}
      onKeyDown={onFocused}
      onClick={onFocused}
      onFocus={onFocused}
      className="grid w-full grid-cols-2 gap-4 rounded-xl bg-theme p-6 shadow-tableShadow"
    >
      <div className="gap-4">
        <Label label="Email" />
        <Input
          value={email}
          handleInputChange={onMemberEmailChange}
          type="text"
          placeholder="Enter email ID"
          error={!!error.email}
          errorMessage={error.email}
          handleInputBlur={onEmailBlur}
        />
      </div>
      <SelectDateInput
        modalTitle="Select Date of Birth"
        label="Date of Birth"
        datePickerLabel="__/__/__"
        handleDateSelect={onDOBChange}
        selectedDate={dateOfBirth}
        isMaxDateRequired
        calendarStyle={`${isLastMonthForDob ? 'disable-next-month' : ''}`}
        onShownDateChange={getIsLastMonthForDob}
        onClearEndDate={onClearDOB}
      />
      {shouldShowBloodGroup && (
        <div>
          <Label label="Blood Group" />
          <Dropdown
            placeholder="Select Blood Group"
            className="h-12.5 w-full rounded-lg !bg-cofeeNeutral1"
            currentFilter={bloodGroup}
            optionsStyle="overflow-y-auto w-full !max-h-56 !overflow-y-auto !top-full !bottom-auto"
            onSelectFilter={onBloodGroupChange}
            filterOptions={bloodGroupOptions}
          />
        </div>
      )}
    </div>
  );
};

export default MemberPersonalInfo;
